import {
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    KeyboardEvent,
} from "react";
import type { Ref } from "react";
import { useDropzone } from "react-dropzone";
import { v7 as uuid } from "uuid";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { contentTypeFromFileName } from "@common/helpers/contentType";
import { ArrowUpIcon, CloseIcon, ClockIcon, DocumentIcon } from "@icons";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import {
    useAgentChatMessageStream,
    type AgentChatLiveResponseState,
} from "@api/agentChatStream.api";
import { useUploadAgentChatAttachment } from "@api/agentChatUploads.api";
import type { PendingSubmission } from "./AgentChatThread";
import {
    AGENT_CHAT_ACCEPTED_MIMES,
    AGENT_CHAT_MAX_FILE_BYTES,
    AGENT_CHAT_MAX_FILES_PER_TURN,
} from "./agentChatAttachmentLimits";
import { FileDropzone } from "@design-system/FileDropzone/FileDropzone";
import { AgentChatAttachmentChip } from "./AgentChatAttachmentChip";
import {
    AGENT_CHAT_FILE_TOO_LARGE_MESSAGE,
    AGENT_CHAT_UNSUPPORTED_FILE_TYPE_MESSAGE,
    getVisibleAttachmentError,
    type AgentChatComposerAttachmentErrorState,
} from "./agentChatComposerState";
import styles from "./AgentChat.module.scss";

export interface AgentChatComposerHandle {
    focus: () => void;
}

interface Props {
    workspaceId: string;
    conversationId: string | null;
    draft: string;
    onDraftChange: (text: string) => void;
    historyCount: number | null;
    onFocus: () => void;
    onCollapse: () => void;
    onClose: () => void;
    onSent: (conversationId: string) => void;
    onPendingChange: (submission: PendingSubmission | null) => void;
    onLiveResponseChange: (live: AgentChatLiveResponseState | null) => void;
}

interface AttachmentState extends AgentChatComposerAttachmentErrorState {
    localId: string;
    file: File;
    progress: number;
    financialDocumentId?: number;
    abortController: AbortController;
}

function getEffectiveMime(file: File): string | undefined {
    if (file.type) {
        return file.type;
    }
    return contentTypeFromFileName(file.name);
}

export const AgentChatComposer = ({
    workspaceId,
    conversationId,
    draft,
    onDraftChange,
    historyCount,
    onFocus,
    onCollapse,
    onClose,
    onSent,
    onPendingChange,
    onLiveResponseChange,
    ref,
}: Props & { ref?: Ref<AgentChatComposerHandle> }) => {
    const stream = useAgentChatMessageStream();
    const uploader = useUploadAgentChatAttachment();
    const { error: toastError } = useToaster();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<AttachmentState[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Bridge the hook's live state up to the widget (sibling of the thread),
    // mirroring the onPendingChange lift. Fires on every event and on reset.
    useEffect(() => {
        onLiveResponseChange(stream.liveResponse);
    }, [stream.liveResponse, onLiveResponseChange]);

    useImperativeHandle(
        ref,
        () => ({
            focus: () => textareaRef.current?.focus(),
        }),
        [],
    );

    // Grow textarea vertically with content. Reset to "auto" first so the
    // textarea can shrink when the user deletes lines; cap at 120 px and
    // let CSS overflow-y handle anything taller.
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [draft]);

    const updateAttachment = (
        localId: string,
        patch: Partial<AttachmentState>,
    ) => {
        setAttachments((current) =>
            current.map((a) =>
                a.localId === localId ? { ...a, ...patch } : a,
            ),
        );
    };

    const startUpload = (entry: AttachmentState) => {
        setErrorMessage(null);
        uploader.mutate(
            {
                workspaceId,
                file: entry.file,
                signal: entry.abortController.signal,
                onProgress: (fraction) =>
                    updateAttachment(entry.localId, { progress: fraction }),
            },
            {
                onSuccess: (result) =>
                    updateAttachment(entry.localId, {
                        status: "ready",
                        progress: 1,
                        financialDocumentId: result.financialDocumentId,
                    }),
                onError: (err) =>
                    updateAttachment(entry.localId, {
                        status: "error",
                        error:
                            err instanceof Error
                                ? err.message
                                : "Upload failed",
                    }),
            },
        );
    };

    const handleFilesAccepted = (incoming: File[]) => {
        const slotsLeft = Math.max(
            0,
            AGENT_CHAT_MAX_FILES_PER_TURN - attachments.length,
        );
        if (incoming.length > slotsLeft) {
            const message = `You can attach up to ${AGENT_CHAT_MAX_FILES_PER_TURN} files per turn`;
            setErrorMessage(message);
            toastError(message);
        }
        const eligible = incoming.slice(0, slotsLeft);

        const newAttachments: AttachmentState[] = [];
        for (const file of eligible) {
            if (file.size > AGENT_CHAT_MAX_FILE_BYTES) {
                const message = AGENT_CHAT_FILE_TOO_LARGE_MESSAGE;
                setErrorMessage(message);
                toastError(message);
                continue;
            }
            const mime = getEffectiveMime(file);
            if (!mime || !AGENT_CHAT_ACCEPTED_MIMES.includes(mime)) {
                const message = AGENT_CHAT_UNSUPPORTED_FILE_TYPE_MESSAGE;
                setErrorMessage(message);
                toastError(message);
                continue;
            }
            newAttachments.push({
                localId: uuid(),
                file,
                status: "uploading",
                progress: 0,
                abortController: new AbortController(),
            });
        }

        if (newAttachments.length === 0) {
            return;
        }
        setAttachments((current) => [...current, ...newAttachments]);
        for (const entry of newAttachments) {
            startUpload(entry);
        }
    };

    const removeAttachment = (localId: string) => {
        const target = attachments.find((a) => a.localId === localId);
        if (target && target.status === "uploading") {
            target.abortController.abort();
        }
        setAttachments((current) =>
            current.filter((a) => a.localId !== localId),
        );
    };

    const submit = async () => {
        const content = draft.trim();
        if (!content || stream.isStreaming) {
            return;
        }
        if (attachments.some((a) => a.status !== "ready")) {
            return;
        }
        setErrorMessage(null);

        // Snapshot what's about to be sent so we can restore it on error.
        const snapshotAttachments = attachments;
        const snapshotPending: PendingSubmission = {
            content,
            attachments: snapshotAttachments.map((a) => ({
                fileName: a.file.name,
                mimeType: getEffectiveMime(a.file) ?? "",
            })),
        };

        // Optimistic: clear composer and surface the pending row immediately.
        onDraftChange("");
        setAttachments([]);
        onPendingChange(snapshotPending);

        try {
            const result = await stream.send({
                workspaceId,
                conversationId,
                message: content,
                attachments: snapshotAttachments.map((a) => ({
                    financialDocumentId: a.financialDocumentId!,
                })),
            });
            onPendingChange(null);
            onSent(result.conversationId);
        } catch (err) {
            // Restore draft + attachments so the user can retry. Clear both
            // the pending bubble and the live response (the stream hook also
            // clears its own live state on rejection).
            onDraftChange(content);
            setAttachments(snapshotAttachments);
            onPendingChange(null);
            onLiveResponseChange(null);
            setErrorMessage(err instanceof Error ? err.message : "Send failed");
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            void submit();
        }
    };

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop: handleFilesAccepted,
        multiple: true,
        noClick: true,
        noKeyboard: true,
    });

    const hasBlockingAttachment = attachments.some((a) => a.status !== "ready");
    const canSend =
        draft.trim().length > 0 &&
        !stream.isStreaming &&
        !hasBlockingAttachment;
    const visibleErrorMessage =
        errorMessage ?? getVisibleAttachmentError(attachments);

    return (
        <FileDropzone
            rootProps={getRootProps()}
            className={styles.pill}
            isDragActive={isDragActive}
            cover={isDragActive ? "Drop files to attach" : null}
            coverClassName={styles.dropOverlay}
        >
            <input {...getInputProps()} />
            {attachments.length > 0 && (
                <div className={styles.attachmentRow}>
                    {attachments.map((attachment) => (
                        <AgentChatAttachmentChip
                            key={attachment.localId}
                            fileName={attachment.file.name}
                            mimeType={getEffectiveMime(attachment.file) ?? ""}
                            mode="live"
                            status={attachment.status}
                            progress={attachment.progress}
                            errorMessage={attachment.error}
                            onRemove={() =>
                                removeAttachment(attachment.localId)
                            }
                        />
                    ))}
                </div>
            )}
            <span className={styles.brand} onDoubleClick={onCollapse}>
                <KickLogo variant="icon" shape="circle" size="lg" />
            </span>
            <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={draft}
                placeholder="Chat with Kick…"
                onFocus={onFocus}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
            />
            <button
                type="button"
                className={styles.iconButton}
                aria-label="Attach file"
                onClick={open}
            >
                <DocumentIcon />
            </button>
            <button
                type="button"
                className={styles.iconButton}
                aria-label="History"
                onClick={onFocus}
            >
                <ClockIcon />
                {historyCount !== null && (
                    <span className={styles.historyBadge}>{historyCount}</span>
                )}
            </button>
            {conversationId && (
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={onClose}
                    aria-label="Close"
                >
                    <CloseIcon />
                </button>
            )}
            <button
                type="button"
                className={styles.sendButton}
                aria-label="Send message"
                onClick={() => void submit()}
                disabled={!canSend}
            >
                <ArrowUpIcon />
            </button>
            {visibleErrorMessage && (
                <span
                    className={styles.errorHint}
                    role="alert"
                    aria-live="polite"
                >
                    {visibleErrorMessage}
                </span>
            )}
        </FileDropzone>
    );
};
